// Composant Wizard de questions — Mode Focus
// 1 question = 1 écran, centré verticalement, auto-advance 300ms
// Navigation : chevrons Typeform-style + dots + clavier (←/→)

import { useState, useCallback, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight, MessageSquare, ArrowDownLeft, Pencil, AlertCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Card, CardContent } from '../ui/card'
import ResponseGrid, { type ReponseValeur } from './ResponseGrid'

export interface Question {
  id: string
  code: string
  libelle: string
  aide?: string
  axe?: string
  axeHaltes?: string
  theme?: string
  ordre: number
}

export interface QuestionWizardProps {
  questions: Question[]
  currentIndex: number
  reponses: Map<string, { valeur?: number; commentaire?: string }>
  onReponseChange: (questionId: string, valeur: ReponseValeur) => void
  onCommentChange?: (questionId: string, comment: string) => void
  onNavigate: (index: number) => void
  onThemeComplete?: () => void
  onThemePrevious?: () => void
  themeName?: string
  /** US-33 (UX) : provenance des réponses pour les métiers 2/3. */
  responseSourceByQuestion?: Map<string, 'prefilled' | 'modified' | 'todo'>
  /** US-33 : nom du métier 1 affiché dans le badge « Repris du métier X ». */
  firstMetierNom?: string
}

// Sous-composant : toggle note discret
function NoteToggle({
  showComment,
  hasComment,
  onToggle,
  commentaire,
  onCommentChange,
}: {
  showComment: boolean
  hasComment: boolean
  onToggle: () => void
  commentaire: string
  onCommentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (showComment && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [showComment])

  return (
    <div className="pt-2">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
      >
        <MessageSquare className="size-3.5" />
        {showComment ? 'Masquer la note' : hasComment ? 'Voir la note' : 'Ajouter une note'}
      </button>

      {showComment && (
        <textarea
          ref={textareaRef}
          value={commentaire}
          onChange={onCommentChange}
          placeholder="Notez ici vos observations, points d'attention..."
          rows={2}
          className="mt-2 w-full max-w-lg mx-auto block text-sm border border-input rounded-lg p-3
            resize-none focus:outline-none focus:ring-2 focus:ring-ring bg-muted/50 focus:bg-background transition-colors"
        />
      )}
    </div>
  )
}

export default function QuestionWizard({
  questions,
  currentIndex,
  reponses,
  onReponseChange,
  onCommentChange,
  onNavigate,
  onThemeComplete,
  onThemePrevious,
  themeName,
  responseSourceByQuestion,
  firstMetierNom,
}: QuestionWizardProps) {
  const [showComment, setShowComment] = useState(false)
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')
  const prevIndexRef = useRef(currentIndex)

  // Détecter la direction de navigation
  useEffect(() => {
    if (currentIndex !== prevIndexRef.current) {
      setDirection(currentIndex > prevIndexRef.current ? 'forward' : 'backward')
      prevIndexRef.current = currentIndex
    }
  }, [currentIndex])

  const currentQuestion = questions[currentIndex]
  if (!currentQuestion) return null

  const reponse = reponses.get(currentQuestion.id)
  const hasComment = Boolean(reponse?.commentaire)
  const isLast = currentIndex === questions.length - 1
  const isFirst = currentIndex === 0

  // Auto-advance timer ref — prevents double-advance on fast clicks
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup timer on index change or unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current)
    }
  }, [currentIndex])

  // Reset comment toggle when navigating
  useEffect(() => {
    setShowComment(false)
  }, [currentIndex])

  // Keyboard navigation (← → or ↑ ↓)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept when typing in textarea or input
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (!isFirst) {
          onNavigate(currentIndex - 1)
        } else if (onThemePrevious) {
          onThemePrevious()
        }
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        if (!isLast) {
          onNavigate(currentIndex + 1)
        } else {
          onThemeComplete?.()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentIndex, isFirst, isLast, onNavigate, onThemePrevious, onThemeComplete])

  const handleReponseChange = useCallback(
    (valeur: ReponseValeur) => {
      onReponseChange(currentQuestion.id, valeur)

      // Cancel any pending auto-advance
      if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current)

      // Auto-advance after 300ms
      autoAdvanceRef.current = setTimeout(() => {
        if (!isLast) {
          onNavigate(currentIndex + 1)
        } else {
          onThemeComplete?.()
        }
      }, 300)
    },
    [currentQuestion.id, onReponseChange, isLast, currentIndex, onNavigate, onThemeComplete]
  )

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onCommentChange?.(currentQuestion.id, e.target.value)
    },
    [currentQuestion.id, onCommentChange]
  )

  const handlePrevious = useCallback(() => {
    if (!isFirst) {
      onNavigate(currentIndex - 1)
    } else if (onThemePrevious) {
      onThemePrevious()
    }
  }, [isFirst, currentIndex, onNavigate, onThemePrevious])

  const handleNext = useCallback(() => {
    if (!isLast) {
      onNavigate(currentIndex + 1)
    } else {
      onThemeComplete?.()
    }
  }, [isLast, currentIndex, onNavigate, onThemeComplete])

  const canGoPrevious = !isFirst || Boolean(onThemePrevious)

  return (
    <Card className="flex flex-col flex-1 overflow-hidden">
      {/* Zone scrollable — contenu centré si ça tient, scroll sinon */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <CardContent className="flex min-h-full items-center justify-center py-4 px-6">
          <div
            key={currentQuestion.id}
            className={cn(
              'max-w-2xl w-full text-center space-y-3',
              direction === 'forward' ? 'animate-slide-in-right' : 'animate-slide-in-left',
            )}
          >
            {/* Thème de la question */}
            {(currentQuestion.theme || themeName) && (
              <p className="text-xs font-semibold text-primary uppercase tracking-widest">
                {currentQuestion.theme || themeName}
              </p>
            )}

            {/* Question */}
            <p className="text-lg font-medium leading-relaxed">
              {currentQuestion.libelle}
            </p>

            {/* US-33 (UX) : badge de provenance pour les métiers 2/3
                (repris du m1 / modifié / à compléter spécifique). */}
            {responseSourceByQuestion && (
              <ResponseSourceBadge
                source={responseSourceByQuestion.get(currentQuestion.id)}
                firstMetierNom={firstMetierNom}
              />
            )}

            {/* Aide — mots-clés */}
            {currentQuestion.aide && (
              <div className="flex items-start gap-2 justify-center bg-muted/50 rounded-lg px-4 py-2.5 max-w-lg mx-auto">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 text-primary text-xs font-bold flex-shrink-0 mt-0.5">?</span>
                <span className="text-sm font-medium text-foreground/70">{currentQuestion.aide}</span>
              </div>
            )}

            {/* Grille de réponses 2x2 */}
            <div className="max-w-lg mx-auto">
              <ResponseGrid
                value={reponse?.valeur}
                onChange={handleReponseChange}
              />
            </div>

            {/* Note discrète */}
            {onCommentChange && (
              <NoteToggle
                showComment={showComment}
                hasComment={hasComment}
                onToggle={() => setShowComment(!showComment)}
                commentaire={reponse?.commentaire || ''}
                onCommentChange={handleCommentChange}
              />
            )}
          </div>
        </CardContent>
      </div>

      {/* Footer : chevrons + dots */}
      <div className="px-6 py-3 border-t border-border">
        <div className="flex items-center justify-center gap-3">
          {/* Chevron gauche */}
          <button
            type="button"
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className={cn(
              "p-1.5 rounded-md transition-colors flex-shrink-0",
              canGoPrevious
                ? "text-muted-foreground hover:text-foreground hover:bg-muted"
                : "text-muted-foreground/20 cursor-not-allowed"
            )}
            title="Question précédente"
          >
            <ChevronLeft className="size-5" />
          </button>

          {/* Dots — navigation dans le thème */}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            {questions.map((q, index) => {
              const hasReponse = reponses.get(q.id)?.valeur !== undefined
              const isCurrent = index === currentIndex

              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => onNavigate(index)}
                  className={cn(
                    "flex-shrink-0 rounded-full transition-all",
                    isCurrent
                      ? "w-6 h-3 bg-primary"
                      : hasReponse
                        ? "w-3 h-3 bg-primary/40"
                        : "w-3 h-3 bg-muted-foreground/20 hover:bg-muted-foreground/40"
                  )}
                  title={`Question ${index + 1}`}
                />
              )
            })}
          </div>

          {/* Chevron droit */}
          <button
            type="button"
            onClick={handleNext}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
            title="Question suivante"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      </div>
    </Card>
  )
}

// US-33 (UX) : indicateur visuel de provenance pour les questions des métiers 2/3.
// 3 états : repris du m1 (bleu), modifié pour ce métier (violet), à compléter (ambre).
function ResponseSourceBadge({
  source,
  firstMetierNom,
}: {
  source?: 'prefilled' | 'modified' | 'todo'
  firstMetierNom?: string
}) {
  if (!source) return null
  const config = {
    prefilled: {
      icon: ArrowDownLeft,
      label: firstMetierNom ? `Repris du métier « ${firstMetierNom} »` : 'Repris du métier 1',
      className: 'border-blue-200 bg-blue-50 text-blue-700',
    },
    modified: {
      icon: Pencil,
      label: 'Modifié pour ce métier',
      className: 'border-purple-200 bg-purple-50 text-purple-700',
    },
    todo: {
      icon: AlertCircle,
      label: 'À compléter (question spécifique à ce métier)',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
    },
  }[source]
  const Icon = config.icon
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border',
        config.className,
      )}
    >
      <Icon className="size-3.5" />
      <span>{config.label}</span>
    </div>
  )
}
